import React, { useCallback, useContext, useMemo, createContext, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';

import { emptyObject, emptyArray } from 'ui-components/default-props/empty';
import { noop } from 'ui-components/default-props/noop';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import { NotificationService, NOTIFICATION_TYPE_ERROR, NOTIFICATION_TYPE_SUCCESS } from 'ui-components/services/notification-service';

import { isStepComplete, mapAsnToSteps, mapStepsToAsn } from './advanced-shipping-notice-context-mappers';
import { useIsStepCollapsed } from './hooks';
import { messages } from './messages';
import {
  DEFINE_SHIPMENTS_STEP,
  HAS_CONFIRMED_DANGEROUS_GOODS,
  ORDERED_ASN_STEPS,
  SELECT_PRODUCTS_STEP,
  SELECTED_PRODUCTS,
  SETUP_CONFIGURATION_STEP,
  SHIPPING_OPTIONS,
} from './steps/constants';
import { useEnableAsyncSyncProductsAndSubmitAsn } from '../../../../../feature-flags/hooks/enable-async-sync-products-and-submit-asn';
import { useSellerShipStationFulfillmentProvider } from '../../../../../hooks/use-shipstation-fulfillment-whitelabel-enabled';
import { ShipStationFulfillmentBarcode } from '../../../../../products/product-details/constants/product-options';
import { createLookupHash } from '../../../../../services/hash-service';
import { LogService } from '../../../../../services/log-service';
import { WorkflowSteps } from '../../../../../tracking/use-workflow-tracking';
import { AsnStatus } from '../../constants';
import { DefineShipmentsSchema } from './steps/define-shipments/form-shema';
import { ProductConfigurationsSchema } from './steps/select-products/form-schema';
import { SetupConfigurationsSchema } from './steps/setup-configurations/form-schema';
import { DocumentType } from '../../../../../enums/document-type';
import { syncProductsStart } from '../../../../../store/advanced-shipping-notices/advanced-shipping-notices.actions';
import { openPrintModal } from '../../../../../store/printing/print.actions';
import { useYup } from '../../../../../validation/use-yup';
import {
  createNewAsn,
  getAsnWithId,
  updateAsn,
  submitAsn,
  submitAsnOnly,
  submitFreightConfiguration,
  getFulfillmentWarehouses,
} from '../../../fulfillments.api';

/** @typedef {import('../../constants').AdvancedShippingNotice} AdvancedShippingNotice */

/**
 * @typedef {object} AdvancedShippingNoticeContextValue
 * @property {(stepId: string) => boolean} isEditingStepWithId - Checks if a step is currently being edited
 * @property {(stepId: string) => void} editStepWithId - Initiates editing for a specific step
 * @property {(stepId: string, payload: object) => Promise<void>} saveStepWithId - Saves the data for a specific step
 * @property {{[key: string]: any}} savedPayloads - The current state of all ASN steps
 * @property {(stepId: string) => boolean} hasStartedStepWithId - Checks if a step has been started
 * @property {(stepId: string) => boolean} isCompleteStepWithId - Checks if a step is complete
 * @property {(callback: () => void) => void} onSubmitAsn - Submits the ASN and calls the provided callback upon completion
 */

const stepToFormSchema = {
  [SETUP_CONFIGURATION_STEP]: SetupConfigurationsSchema,
  [SELECT_PRODUCTS_STEP]: ProductConfigurationsSchema,
  [DEFINE_SHIPMENTS_STEP]: DefineShipmentsSchema,
};

const syncPayloadForStepValidation = (stepId, payload, selectedProductEntities) => {
  if (stepId === SELECT_PRODUCTS_STEP) {
    const productEntitiesMap = createLookupHash(selectedProductEntities, 'productId');
    const mappedProducts = payload.selectedProducts?.map((product) => {
      const productFromEntities = productEntitiesMap[product.productId];
      if (!productFromEntities) {
        return product;
      }
      const barcodeOption = productFromEntities?.productOptions?.find(({ name }) => name === ShipStationFulfillmentBarcode);
      return {
        ...product,
        productOptions: {
          ...product.productOptions,
          [ShipStationFulfillmentBarcode]: barcodeOption?.value,
        },
      };
    });
    return {
      ...payload,
      [SELECTED_PRODUCTS]: mappedProducts,
    };
  }
  return payload;
};

/**
 * Checks if product quantities have changed between previous and current step payloads
 * @param {object} previousStep - The previous step payload
 * @param {object} currentStep - The current step payload
 * @returns {boolean} True if quantities changed, false otherwise
 */
const hasProductQuantitiesChanged = (previousStep, currentStep) => {
  const prevProducts = previousStep?.selectedProducts || [];
  const currProducts = currentStep?.selectedProducts || [];

  if (prevProducts.length !== currProducts.length) return true;

  const currProductsMap = new Map(currProducts.map((p) => [p.productId, p]));

  return prevProducts.some((prevProduct) => {
    const currProduct = currProductsMap.get(prevProduct.productId);
    return !currProduct || prevProduct.quantity !== currProduct.quantity;
  });
};

/**
 * @param {AdvancedShippingNotice} asn
 * @returns {AdvancedShippingNotice}
 */
const removeUnusedProductsFromBoxDefinitions = (asn) => {
  if (!asn.boxes.length) {
    return asn;
  }
  if (!asn.expectedProducts.length) {
    return {
      ...asn,
      boxes: [],
    };
  }
  const productLookup = createLookupHash(asn.expectedProducts, 'productId');
  const newBoxesDefinition = asn.boxes.map((box) => ({
    ...box,
    products: box.products.filter((p) => productLookup[p.productId]),
  }));
  return {
    ...asn,
    boxes: newBoxesDefinition,
  };
};

const areStepsComplete = (steps) => {
  return ORDERED_ASN_STEPS.every((stepId) => {
    const isFloorLoadedContainerConfiguration = steps[SETUP_CONFIGURATION_STEP]?.packageType === SHIPPING_OPTIONS.floorLoadedContainer;
    if (isFloorLoadedContainerConfiguration && stepId === DEFINE_SHIPMENTS_STEP) {
      return true;
    }
    return steps[stepId].isComplete;
  });
};

/** @type {AdvancedShippingNoticeContextValue} */
const defaultContextValue = {
  isEditingStepWithId: () => false,
  editStepWithId: noop,
  // oxlint-disable-next-line no-empty-function
  saveStepWithId: async () => {},
  savedPayloads: {},
  hasStartedStepWithId: () => false,
  isCompleteStepWithId: () => false,
  // oxlint-disable-next-line no-empty-function
  onSubmitAsn: () => {},
};

/** @type {import('react').Context<AdvancedShippingNoticeContextValue>} */
export const AdvancedShippingNoticeContext = createContext(defaultContextValue);

/**
 * Hook to access the Advanced Shipping Notice context
 * @returns {AdvancedShippingNoticeContextValue} The context value
 */
export const useAdvancedShippingNoticeContext = () => useContext(AdvancedShippingNoticeContext);

/**
 * AdvancedShippingNoticeContextProvider component
 *
 * This component provides context for managing the state and actions related to
 * Advanced Shipping Notices (ASN). It handles the logic for editing, saving,
 * and submitting ASN steps, and provides this functionality to its children
 * through context.
 * @param {object} props - Component properties
 * @param {React.ReactNode} props.children - Child components to be rendered within this provider
 * @param {AdvancedShippingNotice} props.initialAsn - Initial Advanced Shipping Notice data
 * @param {(stepId: string | null) => void} props.scrollToNextSection - Function to scroll to the next section
 * @param {(workflowStep: string, data?: object) => void} props.trackAsnWorkflow  - Segment Workflow cb
 * @param {(asnId: string, isComplete: boolean) => void} [props.onSaveStep]  - Callback when a step is saved, isComplete indicates if all steps are complete
 * @returns {JSX.Element} The rendered component
 */
const AdvancedShippingNoticeContextProvider = ({ children, initialAsn, scrollToNextSection, trackAsnWorkflow, onSaveStep }) => {
  const dispatch = useDispatch();
  const [savedPayloads, setSavedPayloads] = useAsyncSafeState(() => mapAsnToSteps(initialAsn));
  const [currentAsnStatus, setCurrentAsnStatus] = useAsyncSafeState(initialAsn.status);
  const isDraft = currentAsnStatus === AsnStatus.Draft;

  const getNextIncompleteStepId = useCallback(
    (currentStepId) => {
      // return the next incomplete step, following the current step
      if (currentStepId && ORDERED_ASN_STEPS.includes(currentStepId)) {
        const startIndex = ORDERED_ASN_STEPS.indexOf(currentStepId) + 1;
        const nextStep = ORDERED_ASN_STEPS.slice(startIndex).find((stepId) => !savedPayloads[stepId]?.isComplete);
        if (nextStep) {
          return nextStep;
        }
      }
      // return the first incomplete step if no current step is provided
      const firstIncompleteStep = ORDERED_ASN_STEPS.find((stepId) => !savedPayloads[stepId]?.isComplete);
      if (firstIncompleteStep) {
        return firstIncompleteStep;
      }
      // return null if all steps are complete
      return null;
    },
    [savedPayloads],
  );

  const [stepIdInEdit, setStepIdInEdit] = useAsyncSafeState(() => getNextIncompleteStepId());
  const isEditingStepWithId = useCallback((stepId) => stepIdInEdit === stepId, [stepIdInEdit]);

  const isCompleteStepWithId = useCallback((stepId) => savedPayloads[stepId].isComplete, [savedPayloads]);

  const [isShippingConfigStepCollapsed, setIsShippingConfigStepCollapsed] = useIsStepCollapsed(
    isDraft,
    isEditingStepWithId(SETUP_CONFIGURATION_STEP),
    isCompleteStepWithId(SETUP_CONFIGURATION_STEP),
  );
  const [isProductStepCollapsed, setIsProductStepCollapsed] = useIsStepCollapsed(
    isDraft,
    isEditingStepWithId(SELECT_PRODUCTS_STEP),
    isCompleteStepWithId(SELECT_PRODUCTS_STEP),
  );
  const [isShipmentsStepCollapsed, setIsShipmentsStepCollapsed] = useIsStepCollapsed(
    isDraft,
    isEditingStepWithId(DEFINE_SHIPMENTS_STEP),
    isCompleteStepWithId(DEFINE_SHIPMENTS_STEP),
  );

  const [currentAsnId, setCurrentAsnId] = useAsyncSafeState(initialAsn.asnId);
  const [isSubmittingASN, setIsSubmittingASN] = useAsyncSafeState(false);
  const [freightConfiguration, setFreightConfiguration] = useAsyncSafeState(initialAsn?.freightConfiguration);
  const [warehouses, setWarehouses] = useAsyncSafeState([]);
  const [isLoadingWarehouses, setIsLoadingWarehouses] = useAsyncSafeState(false);
  const [isSavingStep, setIsSavingStep] = useAsyncSafeState(false);

  const { validate, errors: validationErrors } = useYup({});
  const { sellerFulfillmentProviderId } = useSellerShipStationFulfillmentProvider() || {};
  const shouldAsyncSyncAndSubmit = useEnableAsyncSyncProductsAndSubmitAsn();
  // Fetch warehouses from API
  useEffect(() => {
    const fetchWarehouses = async () => {
      if (!sellerFulfillmentProviderId) return;

      setIsLoadingWarehouses(true);
      try {
        const { warehouses: warehouseData } = await getFulfillmentWarehouses(sellerFulfillmentProviderId);

        // Add key property to each warehouse that matches the warehouse id
        const warehousesWithKey = warehouseData.map((warehouse) => ({
          ...warehouse,
          key: `${warehouse.id}`,
        }));
        setWarehouses(warehousesWithKey);
      } catch (error) {
        LogService.logError(new Error('Error fetching warehouses', { cause: error }), { sellerFulfillmentProviderId });
        NotificationService.showNotification({
          notificationType: NOTIFICATION_TYPE_ERROR,
          title: <FormattedMessage {...messages.errorNotificationWarehousesTitle} />,
          content: <FormattedMessage {...messages.errorNotificationWarehousesMessage} />,
        });
      }
      setIsLoadingWarehouses(false);
    };

    fetchWarehouses();
  }, [sellerFulfillmentProviderId, setWarehouses, setIsLoadingWarehouses]);

  const isParcelConfiguration = savedPayloads[SETUP_CONFIGURATION_STEP]?.packageType === SHIPPING_OPTIONS.parcel;
  const isFloorLoadedContainerConfiguration =
    savedPayloads[SETUP_CONFIGURATION_STEP]?.packageType === SHIPPING_OPTIONS.floorLoadedContainer;

  const isCompleteAllSteps = useMemo(() => areStepsComplete(savedPayloads), [savedPayloads]);

  const editStepWithId = useCallback(
    (stepId) => {
      if (stepId === stepIdInEdit) {
        return setStepIdInEdit(null);
      }

      setSavedPayloads((previousPayloads) => {
        const updatedPayloads = { ...previousPayloads };

        // if the step coming from is complete, set it back to complete before editing the new step
        const stepComingFrom = stepIdInEdit;
        const stepComingFromPayload = updatedPayloads[stepComingFrom];
        if (stepComingFromPayload && isStepComplete(stepComingFromPayload)) {
          updatedPayloads[stepComingFrom] = {
            ...stepComingFromPayload,
            isComplete: true,
          };
        }

        updatedPayloads[stepId] = {
          ...updatedPayloads[stepId],
          isComplete: false,
        };

        return updatedPayloads;
      });

      setStepIdInEdit(stepId);
      if (stepId === SETUP_CONFIGURATION_STEP) {
        setIsShippingConfigStepCollapsed(false);
      } else if (stepId === SELECT_PRODUCTS_STEP) {
        setIsProductStepCollapsed(false);
      } else if (stepId === DEFINE_SHIPMENTS_STEP) {
        setIsShipmentsStepCollapsed(false);
      }
    },
    [
      setSavedPayloads,
      setStepIdInEdit,
      stepIdInEdit,
      setIsShippingConfigStepCollapsed,
      setIsProductStepCollapsed,
      setIsShipmentsStepCollapsed,
    ],
  );

  const doesSaveRequireClearDefineShipments = useMemo(
    () => stepIdInEdit === SETUP_CONFIGURATION_STEP && isCompleteStepWithId(DEFINE_SHIPMENTS_STEP),
    [isCompleteStepWithId, stepIdInEdit],
  );

  const validatePayload = useCallback(
    (payload, selectedProductEntities) => {
      if (stepIdInEdit === SELECT_PRODUCTS_STEP) {
        const selectedProductIds = payload[SELECTED_PRODUCTS]?.map((p) => p.productId) ?? emptyArray;
        const allProductsLoaded = selectedProductIds.every((productId) => {
          const productEntity = selectedProductEntities.find((entity) => entity.productId === productId);
          return productEntity && productEntity.productOptions;
        });

        if (!allProductsLoaded) {
          return true;
        }
      }

      const syncedPayload = syncPayloadForStepValidation(stepIdInEdit, payload, selectedProductEntities);
      return validate(syncedPayload, stepToFormSchema[stepIdInEdit]);
    },
    [stepIdInEdit, validate],
  );

  const refreshAsnAfterImport = useCallback(async () => {
    try {
      const asnData = await getAsnWithId(sellerFulfillmentProviderId, currentAsnId);
      const updatedAsn = mapAsnToSteps(asnData);

      // Mark dependent steps as incomplete for user to review
      if (updatedAsn[SELECT_PRODUCTS_STEP]) {
        updatedAsn[SELECT_PRODUCTS_STEP].isComplete = false;
        // Reset dangerous goods confirmation to enforce validation after import
        updatedAsn[SELECT_PRODUCTS_STEP][HAS_CONFIRMED_DANGEROUS_GOODS] = false;
      }
      if (updatedAsn[DEFINE_SHIPMENTS_STEP]) {
        updatedAsn[DEFINE_SHIPMENTS_STEP].isComplete = false;
      }

      setSavedPayloads(updatedAsn);
    } catch (error) {
      LogService.logError(new Error('Error updating ASN', { cause: error }), { asnId: currentAsnId, sellerFulfillmentProviderId });
      NotificationService.showNotification({
        notificationType: NOTIFICATION_TYPE_ERROR,
        title: <FormattedMessage {...messages.errorNotificationUpdateTitle} />,
        content: <FormattedMessage {...messages.errorNotificationUpdateMessage} />,
      });
    }
  }, [sellerFulfillmentProviderId, setSavedPayloads, currentAsnId]);

  const saveStepWithId = useCallback(
    async (stepId, payload, selectedProductEntities) => {
      const isValid = validatePayload(payload, selectedProductEntities);
      if (!isValid) {
        return;
      }

      const hasQuantityChanged =
        stepId === SELECT_PRODUCTS_STEP &&
        isCompleteStepWithId(DEFINE_SHIPMENTS_STEP) &&
        hasProductQuantitiesChanged(savedPayloads[SELECT_PRODUCTS_STEP], payload);

      const newStepPayload = {
        ...savedPayloads,
        [stepId]: {
          ...payload,
          isComplete: true,
        },
      };

      if (doesSaveRequireClearDefineShipments) {
        newStepPayload[DEFINE_SHIPMENTS_STEP] = emptyObject;
      }

      const mappedFdsPayload = mapStepsToAsn(currentAsnId, newStepPayload);
      const asnPayload = removeUnusedProductsFromBoxDefinitions(mappedFdsPayload);
      trackAsnWorkflow(WorkflowSteps.SAVE_ASN_STEP, {
        stepId,
        asn: asnPayload,
      });

      try {
        setIsSavingStep(true);
        const updatedAsn = currentAsnId
          ? await updateAsn(sellerFulfillmentProviderId, asnPayload)
          : await createNewAsn(sellerFulfillmentProviderId, asnPayload);
        if (onSaveStep) {
          const isComplete = areStepsComplete(newStepPayload);
          onSaveStep(updatedAsn.asnId, isComplete);
        }
        setCurrentAsnId(updatedAsn.asnId);
        setSavedPayloads(mapAsnToSteps(updatedAsn));
        const nextStepId = hasQuantityChanged ? DEFINE_SHIPMENTS_STEP : getNextIncompleteStepId(stepIdInEdit);
        editStepWithId(nextStepId);
        scrollToNextSection(nextStepId);
      } catch (error) {
        trackAsnWorkflow(WorkflowSteps.ERROR, { error });
        LogService.logError(new Error('Error updating ASN', { cause: error }), { asnId: currentAsnId, sellerFulfillmentProviderId });
        NotificationService.showNotification({
          notificationType: NOTIFICATION_TYPE_ERROR,
          title: <FormattedMessage {...messages.errorNotificationUpdateTitle} />,
          content: <FormattedMessage {...messages.errorNotificationUpdateMessage} />,
        });
      } finally {
        setIsSavingStep(false);
      }
    },
    [
      validatePayload,
      savedPayloads,
      doesSaveRequireClearDefineShipments,
      isCompleteStepWithId,
      currentAsnId,
      trackAsnWorkflow,
      setSavedPayloads,
      getNextIncompleteStepId,
      stepIdInEdit,
      editStepWithId,
      scrollToNextSection,
      sellerFulfillmentProviderId,
      setCurrentAsnId,
      setIsSavingStep,
      onSaveStep,
    ],
  );

  const hasStartedStepWithId = useCallback(
    (stepId) => isCompleteStepWithId(stepId) || isEditingStepWithId(stepId),
    [isCompleteStepWithId, isEditingStepWithId],
  );

  const callSubmitAsn = useCallback(
    async (mappedFdsPayload, apiCall) => {
      try {
        const { status } = await apiCall(sellerFulfillmentProviderId, currentAsnId, mappedFdsPayload);

        NotificationService.showNotification({
          notificationType: NOTIFICATION_TYPE_SUCCESS,
          title: <FormattedMessage {...messages.successNotificationSubmitTitle} />,
          content: <FormattedMessage {...messages.successNotificationSubmitMessage} />,
        });
        setCurrentAsnStatus(status);
        trackAsnWorkflow(WorkflowSteps.COMPLETE, { asn: mappedFdsPayload });
        scrollToNextSection(null);

        const url = `${DocumentType.WarehouseLabels.apiDownloadPath}/${currentAsnId}/boxLabels?sellerFillProviderId=${sellerFulfillmentProviderId}`;
        dispatch(openPrintModal(url, DocumentType.WarehouseLabels.identifier));
      } catch (error) {
        trackAsnWorkflow(WorkflowSteps.ERROR, { error });
        LogService.logError(new Error('Error submitting ASN', { cause: error }), { asnId: currentAsnId, sellerFulfillmentProviderId });
        NotificationService.showNotification({
          notificationType: NOTIFICATION_TYPE_ERROR,
          title: <FormattedMessage {...messages.errorNotificationSubmitTitle} />,
          content: <FormattedMessage {...messages.errorNotificationSubmitMessage} />,
        });
      }
    },
    [currentAsnId, sellerFulfillmentProviderId, setCurrentAsnStatus, trackAsnWorkflow, scrollToNextSection, dispatch],
  );

  const onSubmitAsn = useCallback(
    (callback) => {
      const mappedFdsPayload = mapStepsToAsn(currentAsnId, savedPayloads);
      setIsSubmittingASN(true);
      callSubmitAsn(mappedFdsPayload, submitAsn);
      setIsSubmittingASN(false);
      callback();
    },
    [currentAsnId, savedPayloads, setIsSubmittingASN, callSubmitAsn],
  );

  const onSubmitAsnOnly = useCallback(
    (callback) => {
      const mappedFdsPayload = mapStepsToAsn(currentAsnId, savedPayloads);

      const handleStart = () => {
        setIsSubmittingASN(true);
      };

      const handleSuccess = () => {
        callSubmitAsn(mappedFdsPayload, submitAsnOnly);
        setIsSubmittingASN(false);
        callback();
      };

      const handleError = (error) => {
        setIsSubmittingASN(false);
        trackAsnWorkflow(WorkflowSteps.ERROR, { error });
        LogService.logError(new Error('Error submitting ASN', { cause: error }), { asnId: currentAsnId, sellerFulfillmentProviderId });
        NotificationService.showNotification({
          notificationType: NOTIFICATION_TYPE_ERROR,
          title: <FormattedMessage {...messages.errorNotificationSubmitTitle} />,
          content: <FormattedMessage {...messages.errorNotificationSubmitMessage} />,
        });
        callback();
      };

      dispatch(syncProductsStart(sellerFulfillmentProviderId, currentAsnId, mappedFdsPayload, handleStart, handleSuccess, handleError));
    },
    [currentAsnId, savedPayloads, sellerFulfillmentProviderId, setIsSubmittingASN, trackAsnWorkflow, dispatch, callSubmitAsn],
  );

  const onSubmitFreightConfiguration = useCallback(
    async (freightConfig) => {
      try {
        trackAsnWorkflow(WorkflowSteps.SUBMIT_FREIGHT_CONFIGURATION, {
          freightConfig,
        });
        const updatedAsn = await submitFreightConfiguration(sellerFulfillmentProviderId, currentAsnId, freightConfig);
        setFreightConfiguration(updatedAsn?.freightConfiguration);
        NotificationService.showNotification({
          notificationType: NOTIFICATION_TYPE_SUCCESS,
          title: <FormattedMessage {...messages.successNotificationFreightFormTitle} />,
          content: <FormattedMessage {...messages.successNotificationFreightFormMessage} />,
        });
        return true;
      } catch (error) {
        trackAsnWorkflow(WorkflowSteps.ERROR, { error });
        LogService.logError(new Error('Error submitting freight configuration', { cause: error }), {
          asnId: currentAsnId,
          sellerFulfillmentProviderId,
        });
        NotificationService.showNotification({
          notificationType: NOTIFICATION_TYPE_ERROR,
          title: <FormattedMessage {...messages.errorNotificationFreightFormTitle} />,
          content: <FormattedMessage {...messages.errorNotificationFreightFormMessage} />,
        });
        return false;
      }
    },
    [trackAsnWorkflow, sellerFulfillmentProviderId, currentAsnId, setFreightConfiguration],
  );

  const onDownloadBoxLabels = useCallback(() => {
    const url = `${DocumentType.WarehouseLabels.apiDownloadPath}/${currentAsnId}/boxLabels?sellerFillProviderId=${sellerFulfillmentProviderId}`;
    dispatch(openPrintModal(url, DocumentType.WarehouseLabels.identifier));
  }, [currentAsnId, sellerFulfillmentProviderId, dispatch]);

  const value = useMemo(
    () => ({
      refreshAsnAfterImport,
      currentAsnStatus,
      currentAsnId,
      isEditingStepWithId,
      editStepWithId,
      saveStepWithId,
      savedPayloads,
      hasStartedStepWithId,
      isCompleteStepWithId,
      onSubmitAsn: shouldAsyncSyncAndSubmit ? onSubmitAsnOnly : onSubmitAsn,
      isSubmittingASN,
      isParcelConfiguration,
      isFloorLoadedContainerConfiguration,
      isCompleteAllSteps,
      scrollToNextSection,
      initialAsn,
      freightConfiguration,
      isDraft,
      validationErrors,
      onSubmitFreightConfiguration,
      onDownloadBoxLabels,
      warehouses,
      isLoadingWarehouses,
      doesSaveRequireClearDefineShipments,
      validatePayload,
      isSavingStep,
      isShippingConfigStepCollapsed,
      setIsShippingConfigStepCollapsed,
      isProductStepCollapsed,
      setIsProductStepCollapsed,
      isShipmentsStepCollapsed,
      setIsShipmentsStepCollapsed,
    }),
    [
      refreshAsnAfterImport,
      currentAsnStatus,
      currentAsnId,
      isEditingStepWithId,
      editStepWithId,
      saveStepWithId,
      savedPayloads,
      hasStartedStepWithId,
      isCompleteStepWithId,
      onSubmitAsn,
      onSubmitAsnOnly,
      shouldAsyncSyncAndSubmit,
      isSubmittingASN,
      isParcelConfiguration,
      isFloorLoadedContainerConfiguration,
      isCompleteAllSteps,
      scrollToNextSection,
      initialAsn,
      freightConfiguration,
      onSubmitFreightConfiguration,
      validationErrors,
      isDraft,
      onDownloadBoxLabels,
      warehouses,
      isLoadingWarehouses,
      doesSaveRequireClearDefineShipments,
      validatePayload,
      isSavingStep,
      isShippingConfigStepCollapsed,
      setIsShippingConfigStepCollapsed,
      isProductStepCollapsed,
      setIsProductStepCollapsed,
      isShipmentsStepCollapsed,
      setIsShipmentsStepCollapsed,
    ],
  );

  return <AdvancedShippingNoticeContext.Provider value={value}>{children}</AdvancedShippingNoticeContext.Provider>;
};

export default AdvancedShippingNoticeContextProvider;
